/**
 * GanttHeader Component - Date navigation and action buttons for Gantt chart
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, ChevronLeft, ChevronRight, Filter, Plus } from 'lucide-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, getDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PermissionGate from '../PermissionGate';

interface GanttHeaderProps {
  selectedDate: Date;
  showDatePicker: boolean;
  setShowDatePicker: (show: boolean) => void;
  pickerMonth: Date;
  datePickerRef: React.RefObject<HTMLDivElement>;
  onDateSelect: (date: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  onPreviousDay: () => void;
  onNextDay: () => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  activeFiltersCount: number;
}

export const GanttHeader: React.FC<GanttHeaderProps> = ({
  selectedDate,
  showDatePicker,
  setShowDatePicker,
  pickerMonth,
  datePickerRef,
  onDateSelect,
  onPrevMonth,
  onNextMonth,
  onToday,
  onPreviousDay,
  onNextDay,
  showFilters,
  setShowFilters,
  activeFiltersCount,
}) => {
  const navigate = useNavigate();

  const handleBackToCalendar = () => {
    navigate('/cloud/calendario');
  };

  const handleNovaContagem = () => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    navigate(`/cloud/contagens/new?date=${dateStr}`);
  };

  return (
    <div className="mb-6 flex items-center justify-between">
      <div className="flex items-center gap-6">
        <button
          onClick={handleBackToCalendar}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors shadow-sm"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar ao Calendário</span>
        </button>

        {/* Data em Destaque com Navegação e Dropdown Calendar */}
        <div className="relative flex items-center gap-4 bg-white dark:bg-gray-800 p-1.5 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700" ref={datePickerRef}>
          <button
            onClick={onPreviousDay}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-600 dark:text-gray-400"
            title="Dia anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center gap-2 text-gray-900 dark:text-white min-w-[200px] justify-center hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg px-3 py-1 transition-colors group"
            title="Selecionar data"
          >
            <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors" />
            <span className="text-lg font-bold capitalize group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
              {format(selectedDate, "EEEE, dd 'de' MMMM", { locale: ptBR })}
              <span className="text-gray-500 dark:text-gray-400 ml-1 font-normal group-hover:text-blue-600 dark:group-hover:text-blue-400">
                {format(selectedDate, "yyyy", { locale: ptBR })}
              </span>
            </span>
          </button>

          {/* Calendar Dropdown */}
          {showDatePicker && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg shadow-2xl p-4 w-80 animate-fade-in">
              {/* Header - Navegação de mês */}
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={onPrevMonth}
                  className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white capitalize">
                  {format(pickerMonth, 'MMMM yyyy', { locale: ptBR })}
                </h3>
                <button
                  type="button"
                  onClick={onNextMonth}
                  className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
              </div>

              {/* Dias da Semana */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day) => (
                  <div key={day} className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Dias do Mês */}
              <div className="grid grid-cols-7 gap-1">
                {/* Padding para início do mês */}
                {Array.from({ length: getDay(startOfMonth(pickerMonth)) }).map((_, i) => (
                  <div key={`padding-${i}`} className="h-10"></div>
                ))}

                {/* Dias do mês */}
                {eachDayOfInterval({ start: startOfMonth(pickerMonth), end: endOfMonth(pickerMonth) }).map((day) => {
                  const isSelected = isSameDay(day, selectedDate);
                  const isCurrentDay = isToday(day);

                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      onClick={() => onDateSelect(day)}
                      className={`
                        h-10 rounded-lg text-sm font-medium transition-all transform hover:scale-110
                        ${isSelected
                          ? 'bg-blue-600 text-white shadow-lg ring-2 ring-blue-300'
                          : isCurrentDay
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }
                      `}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>

              {/* Botão Hoje */}
              <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={onToday}
                  className="w-full py-2.5 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg text-sm font-semibold transition shadow-md"
                >
                  📅 Hoje
                </button>
              </div>
            </div>
          )}

          <button
            onClick={onNextDay}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-600 dark:text-gray-400"
            title="Próximo dia"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${showFilters || activeFiltersCount > 0
              ? 'bg-blue-600 text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filtros
          {activeFiltersCount > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
              {activeFiltersCount}
            </span>
          )}
        </button>

        <PermissionGate tela="CALENDARIO_VIEW" acao="criar">
          <button
            onClick={handleNovaContagem}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Nova Contagem
          </button>
        </PermissionGate>
      </div>
    </div>
  );
};
